import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import Papa from "papaparse";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { v4 as uuidv4 } from 'uuid';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Upload,
	Download,
	Trash2,
	Check,
	X,
	Loader2,
	Edit,
} from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import type { ParentRegistrationRequest } from "@/lib/client/types.gen";
import {
	getLevelsOptions,
	getSubjectsOptions,
	getTimeSlotsOptions,
	getSchoolsOptions,
	bulkImportParentsMutation,
} from "@/lib/client/@tanstack/react-query.gen";

export const Route = createFileRoute("/admin/parents/import")({
	component: ParentImportPage,
});

interface ParsedRow {
	id: string;
	"Nom Parent": string;
	"Prénom Parent": string;
	"Email Parent": string;
	"Téléphone Parent": string;
	"Adresse Parent": string;
	"Prénom Enfant": string;
	"Nom Enfant": string;
	"Etablissement Enfant": string;
	"Niveau Enfant": string;
	"Classe Enfant": string;
	"Matieres Enfant": string;
	"Créneaux Enfant": string;
	PAP: string;
	PPS: string;
}

function ParentImportPage() {
	const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [importErrors, setImportErrors] = useState<any[]>([]);
	const [importWarnings, setImportWarnings] = useState<any[]>([]);
	const [editingRow, setEditingRow] = useState<ParsedRow | null>(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);

	const { data: levels = [] } = useQuery({
		...getLevelsOptions(),
	});

	const { data: subjects = [] } = useQuery({
		...getSubjectsOptions(),
	});

	const { data: timeSlots = [] } = useQuery({
		...getTimeSlotsOptions(),
	});

	const { data: schools = [] } = useQuery({
		...getSchoolsOptions(),
	});

	const bulkImportMutation = useMutation({
		...bulkImportParentsMutation(),
	});

	const getLevelLabel = (value: string) => {
		return levels.find((l) => l.value === value)?.label || value;
	};

	const getSubjectLabel = (value: string) => {
		return subjects.find((s) => s.value === value)?.label || value;
	};

	const getTimeSlotLabel = (value: string) => {
		return timeSlots.find((t) => t.value === value)?.label || value;
	};

	const getSchoolLabel = (value: string) => {
		if (value?.startsWith("AUTRE")) {
			const match = value.match(/AUTRE\s*\((.*?)\)/);
			if (match && match[1]) {
				return `Autre (${match[1]})`;
			}
			return "Autre";
		}
		return schools.find((s) => s.value === value)?.label || value;
	};

	const downloadExampleFile = () => {
		const exampleData = [
			{
				"Nom Parent": "Dupont",
				"Prénom Parent": "Jean",
				"Email Parent": "jean.dupont@example.com",
				"Téléphone Parent": "0612345678",
				"Adresse Parent": "123 Rue de la Paix, Paris",
				"Prénom Enfant": "Marie",
				"Nom Enfant": "Dupont",
				"Etablissement Enfant": "SAINT_ANDRE",
				"Niveau Enfant": "SIXIEME",
				"Classe Enfant": "6ème A",
				"Matieres Enfant": "MATHS,FRANCAIS",
				"Créneaux Enfant": "SATURDAY_MORNING_1,SATURDAY_MORNING_2",
				PAP: "false",
				PPS: "false",
			},
			{
				"Nom Parent": "Martin",
				"Prénom Parent": "Sophie",
				"Email Parent": "sophie.martin@example.com",
				"Téléphone Parent": "0698765432",
				"Adresse Parent": "45 Avenue des Champs, Lyon",
				"Prénom Enfant": "Lucas",
				"Nom Enfant": "Martin",
				"Etablissement Enfant": "AUTRE (Lycée Victor Hugo)",
				"Niveau Enfant": "TROISIEME",
				"Classe Enfant": "3ème B",
				"Matieres Enfant": "SCIENCES_PHYSIQUE,ANGLAIS",
				"Créneaux Enfant": "SATURDAY_MORNING_1",
				PAP: "true",
				PPS: "false",
			},
		];

		const csv = Papa.unparse(exampleData);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = "exemple_import_parents.csv";
		link.click();
	};

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setIsUploading(true);
		setError(null);
		setSuccess(null);
		setImportErrors([]);
		setImportWarnings([]);

		Papa.parse(file, {
			header: true,
			skipEmptyLines: true,
			complete: (results) => {
				const dataWithIds = results.data.map((row: any, index: number) => ({
					id: `row-${index}`,
					...row,
				}));
				setParsedData(dataWithIds as ParsedRow[]);
				setIsUploading(false);
				setSuccess(`${dataWithIds.length} lignes chargées avec succès`);
			},
			error: (error) => {
				setError(`Erreur lors du parsing du fichier: ${error.message}`);
				setIsUploading(false);
			},
		});
	};

	const removeRow = (id: string) => {
		setParsedData(parsedData.filter((row) => row.id !== id));
	};

	const openEditDialog = (row: ParsedRow) => {
		setEditingRow({ ...row });
		setEditDialogOpen(true);
	};

	const saveEditedRow = () => {
		if (!editingRow) return;

		setParsedData(
			parsedData.map((row) => (row.id === editingRow.id ? editingRow : row)),
		);

		setEditDialogOpen(false);
		setEditingRow(null);
		setSuccess("Ligne modifiée avec succès");
	};

	const updateEditingField = (field: keyof ParsedRow, value: string) => {
		if (!editingRow) return;
		setEditingRow({
			...editingRow,
			[field]: value,
		});
	};

	const toggleSubject = (subjectValue: string) => {
		if (!editingRow) return;
		const currentSubjects = editingRow["Matieres Enfant"]
			? editingRow["Matieres Enfant"]
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
			: [];

		const newSubjects = currentSubjects.includes(subjectValue)
			? currentSubjects.filter((s) => s !== subjectValue)
			: [...currentSubjects, subjectValue];

		updateEditingField("Matieres Enfant", newSubjects.join(","));
	};

	const toggleTimeSlot = (slotValue: string) => {
		if (!editingRow) return;
		const currentSlots = editingRow["Créneaux Enfant"]
			? editingRow["Créneaux Enfant"]
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
			: [];

		const newSlots = currentSlots.includes(slotValue)
			? currentSlots.filter((s) => s !== slotValue)
			: [...currentSlots, slotValue];

		updateEditingField("Créneaux Enfant", newSlots.join(","));
	};

	const getSelectedSubjects = () => {
		if (!editingRow) return [];
		return editingRow["Matieres Enfant"]
			? editingRow["Matieres Enfant"]
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
			: [];
	};

	const getSelectedTimeSlots = () => {
		if (!editingRow) return [];
		return editingRow["Créneaux Enfant"]
			? editingRow["Créneaux Enfant"]
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
			: [];
	};

	const convertToImportFormat = (): ParentRegistrationRequest[] => {
		const parentsByEmail = new Map<string, ParentRegistrationRequest>();

		parsedData.forEach((row) => {
			const email = row["Email Parent"];

			if (!parentsByEmail.has(email)) {
				parentsByEmail.set(email, {
					firstName: row["Prénom Parent"],
					lastName: row["Nom Parent"],
					email: row["Email Parent"],
					phone: row["Téléphone Parent"],
					address: row["Adresse Parent"],
					paymentMethod: "BANK_TRANSFER" as any,
					students: [],
				});
			}

			const parent = parentsByEmail.get(email)!;

			const subjects = row["Matieres Enfant"]
				? row["Matieres Enfant"].split(",").map((s) => s.trim() as any).filter((s) => s && s !== "UNKNOWN")
				: [];

			const timeSlots = row["Créneaux Enfant"]
				? row["Créneaux Enfant"].split(",").map((s) => s.trim() as any).filter((s) => s && s !== "UNKNOWN")
				: [];

			const etablissement = row["Etablissement Enfant"];
			let school: any = etablissement;
			let schoolOther: string | undefined = undefined;

			if (etablissement?.startsWith("AUTRE")) {
				school = "AUTRE";
				const match = etablissement.match(/AUTRE\s*\((.*?)\)/);
				if (match && match[1]) {
					schoolOther = match[1].trim();
				}
			}

			parent.students?.push({
				firstName: row["Prénom Enfant"],
				lastName: row["Nom Enfant"],
				school: school as any,
				schoolOther,
				level: row["Niveau Enfant"] as any,
				className: row["Classe Enfant"] || undefined,
				subjects,
				timeSlots,
				hasPap: row["PAP"]?.toLowerCase() === "true",
				hasPps: row["PPS"]?.toLowerCase() === "true",
				parentId: uuidv4()
			});
		});

		return Array.from(parentsByEmail.values());
	};

	const handleImport = async () => {
		setIsImporting(true);
		setError(null);
		setSuccess(null);
		setImportErrors([]);
		setImportWarnings([]);

		try {
			const parents = convertToImportFormat();

			const response = await bulkImportMutation.mutateAsync({
				body: { parents },
			});

			const hasErrors = response.totalErrors && response.totalErrors > 0;
			const hasWarnings = response.totalWarnings && response.totalWarnings > 0;

			if (hasErrors || hasWarnings) {
				setImportErrors(response.errors || []);
				setImportWarnings(response.warnings || []);

				let message = `Import terminé: ${response.totalImported} parents importés sur ${response.totalRequested}.`;
				if (hasErrors) {
					message += ` ${response.totalErrors} erreur(s).`;
				}
				if (hasWarnings) {
					message += ` ${response.totalWarnings} avertissement(s).`;
				}
				setSuccess(message);
			} else {
				setSuccess(
					`Import réussi: ${response.totalImported} parents sur ${response.totalRequested} importés`,
				);
			}

			setParsedData([]);
		} catch (err: any) {
			setError(err.message || "Erreur lors de l'import des parents");
		} finally {
			setIsImporting(false);
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold">Import en masse de parents</h1>
				<p className="text-gray-600 mt-2">
					Importez plusieurs parents et leurs enfants via un fichier CSV
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Télécharger le fichier exemple</CardTitle>
					<CardDescription>
						Commencez par télécharger le fichier CSV d'exemple pour voir le
						format attendu.
						<br />
						<strong>Format important :</strong>
						<ul className="mt-2 list-disc list-inside text-xs">
							<li>
								Pour établissement AUTRE : utilisez le format "AUTRE (Nom de
								l'établissement)"
							</li>
							<li>
								Pour plusieurs créneaux : séparez-les par des virgules
								"SATURDAY_MORNING_1,SATURDAY_MORNING_2"
							</li>
							<li>
								Pour plusieurs matières : séparez-les par des virgules
								"MATHS,FRANCAIS"
							</li>
							<li>
								La colonne "Classe Enfant" est optionnelle (ex: 6ème A, 3ème B)
							</li>
						</ul>
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Button onClick={downloadExampleFile} variant="outline">
						<Download className="mr-2 h-4 w-4" />
						Télécharger l'exemple CSV
					</Button>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Charger un fichier CSV</CardTitle>
					<CardDescription>
						Sélectionnez un fichier CSV contenant les données des parents et
						enfants
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Input
						type="file"
						accept=".csv"
						onChange={handleFileUpload}
						disabled={isUploading}
					/>
					{isUploading && (
						<div className="mt-4 flex items-center text-sm text-gray-600">
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Chargement du fichier...
						</div>
					)}
				</CardContent>
			</Card>

			{error && (
				<Alert variant="destructive">
					<X className="h-4 w-4" />
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}

			{success && (
				<Alert>
					<Check className="h-4 w-4" />
					<AlertDescription>{success}</AlertDescription>
				</Alert>
			)}

			{importErrors.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="text-red-600">
							Erreurs d'import ({importErrors.length})
						</CardTitle>
						<CardDescription>
							Les lignes suivantes n'ont pas pu être importées
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Ligne</TableHead>
										<TableHead>Nom</TableHead>
										<TableHead>Prénom</TableHead>
										<TableHead>Email</TableHead>
										<TableHead>Raison</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{importErrors.map((err, index) => (
										<TableRow key={index}>
											<TableCell>{err.rowNumber || "-"}</TableCell>
											<TableCell>{err.lastName || "-"}</TableCell>
											<TableCell>{err.firstName || "-"}</TableCell>
											<TableCell>{err.email || "-"}</TableCell>
											<TableCell className="text-red-600">
												{err.errorMessage}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</CardContent>
				</Card>
			)}

			{importWarnings.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="text-orange-600">
							Avertissements ({importWarnings.length})
						</CardTitle>
						<CardDescription>
							Les parents suivants ont été importés mais avec des problèmes
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Ligne</TableHead>
										<TableHead>Nom</TableHead>
										<TableHead>Prénom</TableHead>
										<TableHead>Email</TableHead>
										<TableHead>Détails</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{importWarnings.map((warn, index) => (
										<TableRow key={index}>
											<TableCell>{warn.rowNumber || "-"}</TableCell>
											<TableCell>{warn.lastName || "-"}</TableCell>
											<TableCell>{warn.firstName || "-"}</TableCell>
											<TableCell>{warn.email || "-"}</TableCell>
											<TableCell className="text-orange-600">
												{warn.warningMessage}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</CardContent>
				</Card>
			)}

			{parsedData.length > 0 && (
				<>
					<Card>
						<CardHeader>
							<CardTitle>
								Données chargées ({parsedData.length} lignes)
							</CardTitle>
							<CardDescription>
								Vérifiez et modifiez les données avant l'import
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="overflow-x-auto">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Nom Parent</TableHead>
											<TableHead>Prénom Parent</TableHead>
											<TableHead>Email Parent</TableHead>
											<TableHead>Téléphone Parent</TableHead>
											<TableHead>Prénom Enfant</TableHead>
											<TableHead>Nom Enfant</TableHead>
											<TableHead>Établissement</TableHead>
											<TableHead>Niveau</TableHead>
											<TableHead>Classe</TableHead>
											<TableHead>Matières</TableHead>
											<TableHead>Créneaux</TableHead>
											<TableHead>PAP</TableHead>
											<TableHead>PPS</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{parsedData.map((row) => {
											const matieres = row["Matieres Enfant"]
												? row["Matieres Enfant"]
														.split(",")
														.map((m) => getSubjectLabel(m.trim()))
														.join(", ")
												: "";
											const creneaux = row["Créneaux Enfant"]
												? row["Créneaux Enfant"]
														.split(",")
														.map((c) => getTimeSlotLabel(c.trim()))
														.join(", ")
												: "";

											return (
												<TableRow key={row.id}>
													<TableCell>{row["Nom Parent"]}</TableCell>
													<TableCell>{row["Prénom Parent"]}</TableCell>
													<TableCell>{row["Email Parent"]}</TableCell>
													<TableCell>{row["Téléphone Parent"]}</TableCell>
													<TableCell>{row["Prénom Enfant"]}</TableCell>
													<TableCell>{row["Nom Enfant"]}</TableCell>
													<TableCell>
														{getSchoolLabel(row["Etablissement Enfant"])}
													</TableCell>
													<TableCell>
														{getLevelLabel(row["Niveau Enfant"])}
													</TableCell>
													<TableCell>{row["Classe Enfant"]}</TableCell>
													<TableCell className="text-xs">{matieres}</TableCell>
													<TableCell className="text-xs">{creneaux}</TableCell>
													<TableCell>
														{row["PAP"] === "true" ? "Oui" : "Non"}
													</TableCell>
													<TableCell>
														{row["PPS"] === "true" ? "Oui" : "Non"}
													</TableCell>
													<TableCell>
														<div className="flex gap-2">
															<Button
																variant="ghost"
																size="sm"
																onClick={() => openEditDialog(row)}
															>
																<Edit className="h-4 w-4 text-blue-500" />
															</Button>
															<Button
																variant="ghost"
																size="sm"
																onClick={() => removeRow(row.id)}
															>
																<Trash2 className="h-4 w-4 text-red-500" />
															</Button>
														</div>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
						</CardContent>
					</Card>

					<div className="flex justify-end">
						<Button onClick={handleImport} disabled={isImporting} size="lg">
							{isImporting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Import en cours...
								</>
							) : (
								<>
									<Upload className="mr-2 h-4 w-4" />
									Importer {parsedData.length} lignes
								</>
							)}
						</Button>
					</div>
				</>
			)}

			<Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
				<DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Modifier la ligne</DialogTitle>
						<DialogDescription>
							Modifiez les informations du parent et de l'enfant
						</DialogDescription>
					</DialogHeader>
					{editingRow && (
						<div className="space-y-4">
							<div className="space-y-2">
								<h3 className="font-semibold text-lg">Informations Parent</h3>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label htmlFor="nom-parent">Nom Parent</Label>
										<Input
											id="nom-parent"
											value={editingRow["Nom Parent"]}
											onChange={(e) =>
												updateEditingField("Nom Parent", e.target.value)
											}
										/>
									</div>
									<div>
										<Label htmlFor="prenom-parent">Prénom Parent</Label>
										<Input
											id="prenom-parent"
											value={editingRow["Prénom Parent"]}
											onChange={(e) =>
												updateEditingField("Prénom Parent", e.target.value)
											}
										/>
									</div>
								</div>
								<div>
									<Label htmlFor="email-parent">Email Parent</Label>
									<Input
										id="email-parent"
										type="email"
										value={editingRow["Email Parent"]}
										onChange={(e) =>
											updateEditingField("Email Parent", e.target.value)
										}
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label htmlFor="tel-parent">Téléphone Parent</Label>
										<Input
											id="tel-parent"
											value={editingRow["Téléphone Parent"]}
											onChange={(e) =>
												updateEditingField("Téléphone Parent", e.target.value)
											}
										/>
									</div>
									<div>
										<Label htmlFor="adresse-parent">Adresse Parent</Label>
										<Input
											id="adresse-parent"
											value={editingRow["Adresse Parent"]}
											onChange={(e) =>
												updateEditingField("Adresse Parent", e.target.value)
											}
										/>
									</div>
								</div>
							</div>

							<div className="space-y-2">
								<h3 className="font-semibold text-lg">Informations Enfant</h3>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label htmlFor="nom-enfant">Nom Enfant</Label>
										<Input
											id="nom-enfant"
											value={editingRow["Nom Enfant"]}
											onChange={(e) =>
												updateEditingField("Nom Enfant", e.target.value)
											}
										/>
									</div>
									<div>
										<Label htmlFor="prenom-enfant">Prénom Enfant</Label>
										<Input
											id="prenom-enfant"
											value={editingRow["Prénom Enfant"]}
											onChange={(e) =>
												updateEditingField("Prénom Enfant", e.target.value)
											}
										/>
									</div>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<Label htmlFor="etablissement">Établissement</Label>
										<Select
											value={
												editingRow["Etablissement Enfant"]?.startsWith("AUTRE")
													? "AUTRE"
													: editingRow["Etablissement Enfant"]
											}
											onValueChange={(value) => {
												if (value === "AUTRE") {
													updateEditingField(
														"Etablissement Enfant",
														"AUTRE ()",
													);
												} else {
													updateEditingField("Etablissement Enfant", value);
												}
											}}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un établissement" />
											</SelectTrigger>
											<SelectContent>
												{schools.map((school) => (
													<SelectItem key={school.value} value={school.value}>
														{school.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{editingRow["Etablissement Enfant"]?.startsWith(
											"AUTRE",
										) && (
											<Input
												className="mt-2"
												placeholder="Nom de l'établissement"
												value={
													editingRow["Etablissement Enfant"].match(
														/AUTRE\s*\((.*?)\)/,
													)?.[1] || ""
												}
												onChange={(e) =>
													updateEditingField(
														"Etablissement Enfant",
														`AUTRE (${e.target.value})`,
													)
												}
											/>
										)}
									</div>
									<div>
										<Label htmlFor="niveau">Niveau</Label>
										<Select
											value={editingRow["Niveau Enfant"]}
											onValueChange={(value) =>
												updateEditingField("Niveau Enfant", value)
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un niveau" />
											</SelectTrigger>
											<SelectContent>
												{levels.map((level) => (
													<SelectItem key={level.value} value={level.value}>
														{level.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</div>
								<div>
									<Label htmlFor="classe">Classe (optionnel)</Label>
									<Input
										id="classe"
										value={editingRow["Classe Enfant"] || ""}
										onChange={(e) =>
											updateEditingField("Classe Enfant", e.target.value)
										}
										placeholder="Ex: 6ème A, 3ème B..."
									/>
								</div>
								<div>
									<Label>Matières</Label>
									<div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
										{subjects.map((subject) => {
											const isSelected = getSelectedSubjects().includes(
												subject.value,
											);
											return (
												<div
													key={subject.value}
													className="flex items-center space-x-2"
												>
													<Checkbox
														id={`subject-${subject.value}`}
														checked={isSelected}
														onCheckedChange={() => toggleSubject(subject.value)}
													/>
													<label
														htmlFor={`subject-${subject.value}`}
														className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
													>
														{subject.label}
													</label>
												</div>
											);
										})}
									</div>
									{getSelectedSubjects().length > 0 && (
										<div className="flex flex-wrap gap-2 mt-2">
											{getSelectedSubjects().map((subj) => (
												<Badge key={subj} variant="secondary">
													{subjects.find((s) => s.value === subj)?.label ||
														subj}
												</Badge>
											))}
										</div>
									)}
								</div>
								<div>
									<Label>Créneaux</Label>
									<div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
										{timeSlots.map((slot) => {
											const isSelected = getSelectedTimeSlots().includes(
												slot.value,
											);
											return (
												<div
													key={slot.value}
													className="flex items-center space-x-2"
												>
													<Checkbox
														id={`slot-${slot.value}`}
														checked={isSelected}
														onCheckedChange={() => toggleTimeSlot(slot.value)}
													/>
													<label
														htmlFor={`slot-${slot.value}`}
														className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
													>
														{slot.label}
													</label>
												</div>
											);
										})}
									</div>
									{getSelectedTimeSlots().length > 0 && (
										<div className="flex flex-wrap gap-2 mt-2">
											{getSelectedTimeSlots().map((ts) => (
												<Badge key={ts} variant="secondary">
													{timeSlots.find((t) => t.value === ts)?.label || ts}
												</Badge>
											))}
										</div>
									)}
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div className="flex items-center space-x-2">
										<Checkbox
											id="pap"
											checked={editingRow["PAP"]?.toLowerCase() === "true"}
											onCheckedChange={(checked) =>
												updateEditingField("PAP", checked ? "true" : "false")
											}
										/>
										<label
											htmlFor="pap"
											className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
										>
											PAP (Plan d'Accompagnement Personnalisé)
										</label>
									</div>
									<div className="flex items-center space-x-2">
										<Checkbox
											id="pps"
											checked={editingRow["PPS"]?.toLowerCase() === "true"}
											onCheckedChange={(checked) =>
												updateEditingField("PPS", checked ? "true" : "false")
											}
										/>
										<label
											htmlFor="pps"
											className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
										>
											PPS (Projet Personnalisé de Scolarisation)
										</label>
									</div>
								</div>
							</div>
						</div>
					)}
					<DialogFooter>
						<Button variant="outline" onClick={() => setEditDialogOpen(false)}>
							Annuler
						</Button>
						<Button onClick={saveEditedRow}>Enregistrer</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
